import Button from "components/foundation/Button";
import React, { HTMLAttributes } from "react";
import styled, { css } from "styled-components";

export const CustomTable = styled.div<{ width?: string; columns?: string }>`
  display: grid;

  width: ${({ width }) => width ?? "100%"};

  border-bottom: 1px solid ${({ theme }) => theme.color.stroke.dark};

  grid-template-columns: ${(props) => props.columns || "auto"};
`;

export const TableHeader = styled.th<{ width?: string }>`
  ${({ theme, width }) => css`
    padding: 1.4rem 2.4rem;
    background-color: ${theme.color.table.header};
    color: ${theme.color.text.white};
    text-align: left;

    ${theme.typography.body2.medium}
    box-sizing: border-box;
    width: ${width};

    ${theme.media.small} {
      ${theme.typography.caption1.medium}
      padding: 0.8rem;
    }
  `}
`;

const TableThead = styled.thead`
  display: contents;

  ${({ theme }) => theme.typography.body2.medium}
  color: ${({ theme }) => theme.color.text.white};
`;

const TableBody = styled.tbody`
  display: contents;

  tr:nth-child(even) {
    td {
      background-color: ${({ theme }) => theme.color.table.strip};
    }
  }
`;

const TableRow = styled.tr`
  display: contents;
  border-bottom: 1px solid ${({ theme }) => theme.color.grey[300]};
`;

const CustomTd = styled.td<{ attention?: boolean }>`
  padding: 0rem 2rem;
  height: 6.4rem;
  display: flex;
  align-items: center;
  display: "flex";
  overflow: hidden;

  ${({ theme }) => css`
    ${theme.media.small} {
      ${theme.typography.caption1.regular}
      padding:1.2rem 0.8rem;
    }
  `};

  ${({ attention }) =>
    attention &&
    css`
      color: ${({ theme }) => theme.color.text.danger};
    `}
`;

const TableCol = ({
  attention,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { attention?: boolean }) => {
  return (
    <CustomTd attention={attention}>
      <div
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        {...rest}
      >
        {rest.children}
      </div>
    </CustomTd>
  );
};
export const TableLayout = styled.div`
  width: 100%;
  overflow-x: auto;

  /* 가로 스크롤 가능함을 보이도록 스크롤바 노출 (넘칠 때만 표시) */
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: ${({ theme }) => theme.color.grey[400]} transparent;

  &::-webkit-scrollbar {
    height: 0.8rem;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.color.grey[400]};
    border-radius: 0.4rem;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const TableTotalCount = styled.h3`
  ${({ theme }) => css`
    color: ${theme.color.grey[700]};
    font-size: 15px;
    font-weight: 600;
    line-height: 24px; /* 160% */
    letter-spacing: -0.45px;

    white-space: nowrap;

    ${theme.media.small} {
      ${theme.typography.caption1.semibold}
    }
  `}
`;

const TableLayoutHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  margin-bottom: 1rem;
`;

const TableButton = styled(Button)`
  padding: 0.4rem 1.2rem;
  ${({ theme }) => theme.typography.caption1.regular};

  ${({ theme }) => css`
    &:hover {
      color: ${theme.color.text.secondary};
      border: 1px solid ${theme.color.stroke.dark};
    }
  `}
`;

export const Table = {
  Header: TableLayoutHeader,
  TotalCount: TableTotalCount,
  Layout: TableLayout,
  Table: CustomTable,
  Thead: TableThead,
  Th: TableHeader,
  Tr: TableRow,
  Td: TableCol,
  Tbody: TableBody,
  Button: TableButton,
};
